import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";

interface CoverageData {
  name: string;
  id?: string;
  uniqueDoctor: number;
  visitFrequencyPer: string;
  missedCall: number;
  doctorCallAverage: number;
  noOfDoctorCall: number;
}

interface FieldWorkData {
  name: string;
  id?: string;
  mr_Working_Days: number;
  flm_Mr_Working_Days: number;
}

type TableData = CoverageData | FieldWorkData;

interface SummaryTableProps {
  title: string;
  data: TableData[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({ title, data }) => {
  const isCoverageData = (item: TableData): item is CoverageData => {
    return "name" in item && "uniqueDoctor" in item;
  };

  const coverageHeaders = [
    "Unique Doctor",
    "Visit Frequency Compliance",
    "Missed Call",
    "Doctor Call Average",
    "No. of Doctor Call ",
  ];

  const fieldWorkHeaders = ["MR Working Days", "FLM MR Working Days"];

  const getHeaderKey = (item: TableData) => {
    return item.id ? `${item.name} (${item.id})` : item.name;
  };

  const renderCellValue = (item: TableData, header: string) => {
    if (isCoverageData(item)) {
      switch (header) {
        case "Unique Doctor":
          return item.uniqueDoctor;
        case "Visit Frequency Compliance":
          return `${Number(item.visitFrequencyPer).toFixed(2)}%`;
        case "Missed Call":
          return item.missedCall;
        case "Doctor Call Average":
          return item.doctorCallAverage;
        case "No. of Doctor Call ":
          return item.noOfDoctorCall;
        default:
          return "";
      }
    } else {
      switch (header) {
        case "MR Working Days":
          return item.mr_Working_Days;
        case "FLM MR Working Days":
          return item.flm_Mr_Working_Days;
        default:
          return "";
      }
    }
  };

  const headers =
    data.length > 0 && isCoverageData(data[0])
      ? coverageHeaders
      : fieldWorkHeaders;

  if (!data.length) {
    return (
      <Card>
        <CardContent>
          <Typography
            variant="body1"
            sx={{
              fontSize: "24px",
              fontWeight: "bold",
              marginRight: 2,
              minWidth: "50px",
            }}
          >
            {title}
          </Typography>
          <Typography>No data available</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <CardContent sx={{ padding: "16px 0", borderRadius: "none" }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: "24px",
            fontWeight: "bold",
            marginRight: 2,
            minWidth: "50px",
          }}
        >
          {title}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", borderRadius: "0" }}
        >
          <Table
            sx={{ minWidth: 650 }}
            aria-label={`${title.toLowerCase()} table`}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "#297F9D" }}>
                <TableCell
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    border: "1px solid #297F9D",
                  }}
                ></TableCell>
                {data.map((row, index) => (
                  <TableCell
                    key={index}
                    sx={{
                      color: "white",
                      fontWeight: "bold",
                      backgroundColor: "#297F9D",
                      border: "1px solid #297F9D",
                    }}
                    align="center"
                  >
                    {getHeaderKey(row)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {headers.map((header, headerIndex) => (
                <TableRow key={headerIndex}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      border: "1px solid #1895AD",
                    }}
                  >
                    {header}
                  </TableCell>
                  {data.map((row, index) => (
                    <TableCell
                      key={index}
                      align="center"
                      sx={{
                        border: "1px solid #1895AD",
                        backgroundColor:
                          index === data.length - 1 ? "#F9D23D" : "inherit",
                      }}
                    >
                      {renderCellValue(row, header)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Box>
  );
};

export default SummaryTable;
